import PaginatedComponent from 'core/PaginatedComponent';
import { createCustomSlice } from 'core/utils';
import i18n from 'i18nextInstance';

const initialState = {
    donation: null,
    donationLoading: false,
    donationSuccess: null,
    errors: {},
    invitationSuccess: false,
    list: null,
    loading: false,
    participants: null,
    participantsDownload: [],
    participantsDownloading: false,
    participantsLoading: false,
    participantsStatus: null,
    processing: false,
    selected: null,
    training: null,
    trainingLoading: false,
    trainingSuccess: null,
    vaccination: null,
    vaccinationLoading: false,
    vaccinationSuccess: null
};

const campaignSlice = createCustomSlice({
    initialState,
    name: 'campaign',
    reducers: {
        setCampaignList (state, { payload }) {
            state.list = new PaginatedComponent({
                ...payload,
                dataParser: parseCampaignList,
                loadingAction: campaignActions.setCampaignLoading,
                setAction: campaignActions.setCampaignList
            });
        },
        setCampaignParticipants (state, { payload }) {
            state.participants = new PaginatedComponent({
                ...payload,
                dataParser: parseCampaignParticipants,
                loadingAction: campaignActions.setCampaignParticipantsLoading,
                setAction: campaignActions.setCampaignParticipants
            });
        }
    },
    saga: [
        'changeParticipantStatus',
        'getCampaign',
        'getCampaignParticipants',
        'getCampaignParticipantsDownload',
        'getCampaigns',
        'getDonation',
        'getTraining',
        'getVaccination',
        'inviteParticipants',
        'upsertCampaign',
        'upsertDonation',
        'upsertTraining',
        'upsertVaccination'
    ]
});

const parseCampaignName = campaign => {
    const { address, name, survey, type, vaccine } = campaign;
    const mapper = {
        [CAMPAIGN_TYPES.communication]: name,
        [CAMPAIGN_TYPES.donation]: address,
        [CAMPAIGN_TYPES.healthSurvey]: i18n.t('Cuestionario de salud'),
        [CAMPAIGN_TYPES.quiz]: survey?.name,
        [CAMPAIGN_TYPES.training]: name,
        [CAMPAIGN_TYPES.vaccination]: vaccine?.name
    };
    return mapper[type] ?? '-';
};

const parseCampaignList = list => list.map(c => ({
    ...c,
    parsedDatetime: c.effective_datetime ?? c.start_datetime ?? null,
    parsedName: parseCampaignName(c)
}));

export const parseCampaignParticipants = (list = []) => list.map(participant => {
    const {
        id,
        patient,
        patient: {
            id: patient_id
        },
        status
    } = participant;
    const parsedParticipant = {
        ...participant,
        ...patient,
        id,
        patient_id,
        status_label: parseCampaignParticipantStatus(status)?.label
    };
    delete parsedParticipant.patient;
    return parsedParticipant;
});

export const parseCampaignParticipantStatus = status => {
    const properties = {
        absent: {
            label: i18n.t('Ausente'),
            style: {
                color: 'orange'
            }
        },
        accepted: {
            label: i18n.t('Aceptado'),
            style: {
                color: 'green'
            }
        },
        invited: {
            label: i18n.t('Invitado'),
            style: {
                color: 'grey'
            }
        },
        present: {
            label: i18n.t('Presente'),
            style: {
                color: 'green'
            }
        },
        registered: {
            label: i18n.t('Registrado'),
            style: {
                fontWeight: 'bold'
            }
        },
        rejected: {
            label: i18n.t('Rechazado'),
            style: {
                color: 'red'
            }
        }
    };
    return properties[status] ?? {
        label: '-',
        style: {}
    };
};

export const parseCampaignType = type => {
    const types = {
        communication: i18n.t('Comunicación'),
        donation: i18n.t('Donación'),
        health_survey: i18n.t('Cuestionario de salud'),
        quiz: i18n.t('Encuesta'),
        training: i18n.t('Capacitación'),
        vaccination: i18n.t('Vacunación')
    };
    return types[type] ?? '-';
};

export const CAMPAIGN_TYPES = {
    communication: 'communication',
    donation: 'donation',
    healthSurvey: 'health_survey',
    quiz: 'quiz',
    training: 'training',
    vaccination: 'vaccination'
};

export const campaignActions = campaignSlice.actions;

export default campaignSlice.reducer;
